/**
 * Coups de pouce — Attendu 660 « Résoudre des problèmes avec la propriété
 * caractéristique de la médiatrice ».
 *
 * Une entrée par `generator_key` (cf. gen_660.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES valeurs que
 * celles que le générateur peut tirer, sinon elle donne la réponse. Chaque coup
 * de pouce est autonome : les formats sont indépendants et un exercice peut ne
 * jamais être donné.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : deux points de la médiatrice, deux valeurs ──
// 9 cm et 12 cm : le générateur tire ses distances entre 3 et 8 cm, les valeurs
// sont donc hors de portée. DEUX points et non un seul, parce que le distracteur
// du format est « BM change selon la position de M » : la figure doit montrer
// que l'égalité tient pour chaque point, mais que sa valeur, elle, change bien.
// Les longueurs dessinées respectent le rapport 12/9 (142 px contre 108 px).
const SVG_DEUX_POINTS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 245" role="img"
     aria-label="Un segment RS et sa mediatrice. Le point P, haut sur la droite, verifie PR egal PS egal 12 cm. Le point Q, plus bas, verifie QR egal QS egal 9 cm.">
  <line x1="180" y1="20" x2="180" y2="200" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <text x="188" y="33" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <line x1="180" y1="60" x2="90" y2="170" stroke="#9333EA" stroke-width="1.5"/>
  <line x1="180" y1="60" x2="270" y2="170" stroke="#9333EA" stroke-width="1.5"/>
  <line x1="180" y1="110" x2="90" y2="170" stroke="#E24B4A" stroke-width="1.5"/>
  <line x1="180" y1="110" x2="270" y2="170" stroke="#E24B4A" stroke-width="1.5"/>

  <line x1="90" y1="170" x2="270" y2="170" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <polyline points="192,170 192,158 180,158" fill="none" stroke="#F97316" stroke-width="1.6"/>
  <line x1="135" y1="164" x2="135" y2="176" stroke="#F97316" stroke-width="1.8"/>
  <line x1="225" y1="164" x2="225" y2="176" stroke="#F97316" stroke-width="1.8"/>

  <circle cx="90" cy="170" r="4" fill="#0f172a"/>
  <circle cx="270" cy="170" r="4" fill="#0f172a"/>
  <circle cx="180" cy="60" r="4.5" fill="#9333EA"/>
  <circle cx="180" cy="110" r="4.5" fill="#E24B4A"/>

  <text x="80" y="189" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="278" y="189" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="171" y="53" text-anchor="end" font-size="12.5" font-weight="700" fill="#9333EA">P</text>
  <text x="171" y="104" text-anchor="end" font-size="12.5" font-weight="700" fill="#E24B4A">Q</text>

  <text x="127" y="112" text-anchor="end" font-size="11.5" font-weight="700" fill="#9333EA">12 cm</text>
  <text x="233" y="112" font-size="11.5" font-weight="700" fill="#9333EA">12 cm</text>
  <text x="127" y="152" text-anchor="end" font-size="11.5" font-weight="700" fill="#E24B4A">9 cm</text>
  <text x="233" y="152" font-size="11.5" font-weight="700" fill="#E24B4A">9 cm</text>

  <text x="180" y="220" text-anchor="middle" font-size="11.5" font-weight="700" fill="#0f172a">chaque point de (d) a ses deux longueurs égales</text>
  <text x="180" y="237" text-anchor="middle" font-size="11.5" fill="#334155">mais leur valeur change d'un point à l'autre</text>
</svg>`.trim();

// ── Figure Ex. 4 : équidistant ne veut pas dire sur le cercle ──
// Les trois lignes fausses du format confondent médiatrice et cercle. La figure
// montre donc un point P équidistant de R et S, posé sur la médiatrice mais
// FRANCHEMENT hors du cercle. Le codage reprend la convention de l'exercice :
// un tiret pour les rayons, deux pour l'équidistance.
const SVG_MEDIATRICE_HORS_CERCLE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" role="img"
     aria-label="Un cercle de centre O, une corde RS, et la mediatrice de RS qui passe par O et sort du cercle. Le point P est sur cette mediatrice, hors du cercle : PR egale PS.">
  <line x1="180" y1="20" x2="180" y2="205" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <circle cx="180" cy="130" r="68" fill="none" stroke="#0f172a" stroke-width="2"/>

  <line x1="180" y1="130" x2="116.1" y2="106.7" stroke="#F97316" stroke-width="1.5"/>
  <line x1="180" y1="130" x2="243.9" y2="106.7" stroke="#F97316" stroke-width="1.5"/>
  <line x1="180" y1="40" x2="116.1" y2="106.7" stroke="#E24B4A" stroke-width="1.5"/>
  <line x1="180" y1="40" x2="243.9" y2="106.7" stroke="#E24B4A" stroke-width="1.5"/>
  <line x1="116.1" y1="106.7" x2="243.9" y2="106.7" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>

  <polyline points="192,106.7 192,94.7 180,94.7" fill="none" stroke="#0f172a" stroke-width="1.4"/>

  <line x1="149.8" y1="113.7" x2="146.3" y2="123" stroke="#F97316" stroke-width="1.8"/>
  <line x1="213.7" y1="123" x2="210.2" y2="113.7" stroke="#F97316" stroke-width="1.8"/>

  <line x1="145.8" y1="68.4" x2="153" y2="75.4" stroke="#E24B4A" stroke-width="1.8"/>
  <line x1="143.1" y1="71.3" x2="150.3" y2="78.3" stroke="#E24B4A" stroke-width="1.8"/>
  <line x1="214.2" y1="68.4" x2="207" y2="75.4" stroke="#E24B4A" stroke-width="1.8"/>
  <line x1="216.9" y1="71.3" x2="209.7" y2="78.3" stroke="#E24B4A" stroke-width="1.8"/>

  <circle cx="180" cy="130" r="4" fill="#0f172a"/>
  <circle cx="116.1" cy="106.7" r="4" fill="#0f172a"/>
  <circle cx="243.9" cy="106.7" r="4" fill="#0f172a"/>
  <circle cx="180" cy="40" r="4.5" fill="#E24B4A"/>

  <text x="188" y="138" font-size="12.5" font-weight="700" fill="#0f172a">O</text>
  <text x="108" y="113" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="252" y="113" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="171" y="34" text-anchor="end" font-size="12.5" font-weight="700" fill="#E24B4A">P</text>

  <text x="180" y="226" text-anchor="middle" font-size="11.5" font-weight="700" fill="#0f172a">P est à égale distance de R et de S</text>
  <text x="180" y="243" text-anchor="middle" font-size="11.5" fill="#334155">et pourtant P n'est pas sur le cercle</text>
</svg>`.trim();

// ── Figure Ex. 5 : deux médiatrices et leur intersection ──
// L'exercice fait compléter des justifications : toute règle écrite fournirait
// « médiatrice » et « équidistant », les deux mots les plus tirés. L'aide est
// donc une fiche. Elle montre la situation des modèles A et F : deux
// médiatrices, leur intersection, et les trois longueurs égales qui en font le
// centre du cercle. Le vocabulaire attendu est lisible sur la figure, mais
// l'élève doit le replacer lui-même dans la phrase.
const SVG_DEUX_MEDIATRICES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 262" role="img"
     aria-label="Trois points R, S et T. La mediatrice de RS et celle de ST se coupent en O. Les longueurs OR, OS et OT sont egales : O est le centre du cercle passant par les trois points.">
  <circle cx="180" cy="130" r="72" fill="none" stroke="#CBD5E1" stroke-width="1.4"/>

  <line x1="163.3" y1="41.4" x2="196.7" y2="218.6" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <line x1="88.2" y1="105.4" x2="271.8" y2="154.6" stroke="#9333EA" stroke-width="1.6" stroke-dasharray="7 5"/>

  <line x1="180" y1="130" x2="112.3" y2="105.4" stroke="#F97316" stroke-width="1.5"/>
  <line x1="180" y1="130" x2="235.2" y2="83.7" stroke="#F97316" stroke-width="1.5"/>
  <line x1="180" y1="130" x2="204.6" y2="197.7" stroke="#F97316" stroke-width="1.5"/>

  <line x1="142.6" y1="112.5" x2="149.7" y2="122.9" stroke="#F97316" stroke-width="1.8"/>
  <line x1="203.4" y1="102.6" x2="211.8" y2="111.1" stroke="#F97316" stroke-width="1.8"/>
  <line x1="185.1" y1="160.6" x2="199.5" y2="167.1" stroke="#F97316" stroke-width="1.8"/>

  <circle cx="180" cy="130" r="4.5" fill="#0f172a"/>
  <circle cx="112.3" cy="105.4" r="4" fill="#0f172a"/>
  <circle cx="235.2" cy="83.7" r="4" fill="#0f172a"/>
  <circle cx="204.6" cy="197.7" r="4" fill="#0f172a"/>

  <text x="187" y="126" font-size="12.5" font-weight="700" fill="#0f172a">O</text>
  <text x="104" y="112" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="243" y="80" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="212" y="210" font-size="12.5" font-weight="700" fill="#0f172a">T</text>

  <text x="168" y="36" font-size="10" font-weight="700" fill="#378ADD">médiatrice de [RS]</text>
  <text x="10" y="95" font-size="10" font-weight="700" fill="#9333EA">médiatrice de [ST]</text>

  <text x="180" y="238" text-anchor="middle" font-size="11.5" font-weight="700" fill="#0f172a">les deux médiatrices se coupent en O</text>
  <text x="180" y="255" text-anchor="middle" font-size="11.5" fill="#334155">O est à égale distance de R, S et T</text>
</svg>`.trim();

// ── Figure Ex. 5 (variante compas) : les deux arcs de même écartement ──
// Le modèle mD décrit le geste du compas. La fiche des deux médiatrices ne lui
// sert à rien : on montre donc le geste lui-même — un arc depuis R, un arc de
// MÊME écartement depuis S, leurs deux points d'intersection, et la droite qui
// les joint. Les arcs sont des arcs (et non des cercles entiers) parce que
// c'est ce que trace un compas sur le papier.
const SVG_COMPAS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 258" role="img"
     aria-label="Un segment RS. Un arc trace depuis R et un arc de même écartement trace depuis S se coupent en deux points. La droite qui joint ces deux points est la mediatrice de RS.">
  <path d="M 156.6 66.3 A 90 90 0 0 1 156.6 213.7" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <path d="M 203.4 66.3 A 90 90 0 0 0 203.4 213.7" fill="none" stroke="#F97316" stroke-width="1.8"/>

  <line x1="180" y1="75" x2="180" y2="205" stroke="#1D9E75" stroke-width="2.2"/>

  <line x1="105" y1="140" x2="255" y2="140" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <circle cx="105" cy="140" r="4" fill="#0f172a"/>
  <circle cx="255" cy="140" r="4" fill="#0f172a"/>
  <circle cx="180" cy="90.3" r="4.5" fill="#1D9E75"/>
  <circle cx="180" cy="189.7" r="4.5" fill="#1D9E75"/>

  <text x="97" y="159" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="263" y="159" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="180" y="40" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">les deux arcs ont le même écartement</text>

  <text x="180" y="232" text-anchor="middle" font-size="10.5" font-weight="700" fill="#1D9E75">les arcs se coupent en deux points équidistants de R et S</text>
  <text x="180" y="249" text-anchor="middle" font-size="11.5" fill="#334155">la droite qui les joint est la médiatrice de [RS]</text>
</svg>`.trim();


// ── Figure Ex. 5 (variante corde) : rayons egaux, mediatrice, milieu ──
// Le modele mB enchaine trois faits : OS = OA parce que ce sont des RAYONS,
// donc O est sur la MEDIATRICE de la corde, donc cette médiatrice coupe la
// corde en son MILIEU a angle droit. La figure porte les trois codages, chacun
// dans sa couleur : orange pour l egalite des rayons, rouge pour les deux
// moities de la corde, noir pour l angle droit.
const SVG_CORDE_RAYONS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 254" role="img"
     aria-label="Un cercle de centre O et une corde SA. Les rayons OS et OA sont egaux. La mediatrice de SA passe par O et coupe la corde en son milieu I, a angle droit.">
  <line x1="180" y1="25" x2="180" y2="210" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <text x="188" y="38" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <circle cx="180" cy="120" r="75" fill="none" stroke="#0f172a" stroke-width="2"/>

  <line x1="180" y1="120" x2="120" y2="165" stroke="#F97316" stroke-width="1.6"/>
  <line x1="180" y1="120" x2="240" y2="165" stroke="#F97316" stroke-width="1.6"/>
  <line x1="147" y1="138.5" x2="153" y2="146.5" stroke="#F97316" stroke-width="1.8"/>
  <line x1="207" y1="138.5" x2="213" y2="146.5" stroke="#F97316" stroke-width="1.8"/>

  <line x1="120" y1="165" x2="240" y2="165" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="150" y1="159" x2="150" y2="171" stroke="#E24B4A" stroke-width="2"/>
  <line x1="210" y1="159" x2="210" y2="171" stroke="#E24B4A" stroke-width="2"/>
  <polyline points="192,165 192,177 180,177" fill="none" stroke="#0f172a" stroke-width="1.4"/>

  <circle cx="180" cy="120" r="4.5" fill="#0f172a"/>
  <circle cx="120" cy="165" r="4" fill="#0f172a"/>
  <circle cx="240" cy="165" r="4" fill="#0f172a"/>
  <circle cx="180" cy="165" r="4" fill="#0f172a"/>

  <text x="188" y="116" font-size="12.5" font-weight="700" fill="#0f172a">O</text>
  <text x="112" y="182" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="248" y="182" font-size="12.5" font-weight="700" fill="#0f172a">A</text>
  <text x="168" y="182" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">I</text>

  <text x="180" y="230" text-anchor="middle" font-size="11" font-weight="700" fill="#0f172a">OS = OA (rayons), donc O est sur la médiatrice de [SA]</text>
  <text x="180" y="246" text-anchor="middle" font-size="11" fill="#334155">cette médiatrice coupe [SA] en son milieu, à angle droit</text>
</svg>`.trim();

/**
 * `contenu` arrive parfois en JSON brut (cf. CorrectionItem.jsx) : on lit la
 * phrase du tirage sans jamais supposer qu'elle est déjà parsée.
 */
const phraseDe = (question) => {
    const c = question?.contenu;
    if (!c) return '';
    if (typeof c === 'string') {
        try { return JSON.parse(c)?.phrase || ''; } catch (_e) { return c; }
    }
    return c.phrase || '';
};

const CP_660 = {
    pb_lieu_equidistant_qcm: {
        pages: [
            {
                titre: 'La règle',
                // Ancienne version : la propriété énoncée telle quelle, donc
                // l'option correcte recopiée. On la remplace par le tracé qui la
                // fait apparaître.
                lignes: [
                    'Place les deux lieux sur un brouillon, puis marque à la règle **plusieurs** positions situées à égale distance des deux.',
                    'Observe comment ces positions se placent les unes par rapport aux autres, et jusqu\'où elles vont.',
                    'La bonne réponse doit contenir **toutes** ces positions : écarte celles qui n\'en désignent qu\'une, et celles qui contiennent des points plus proches de l\'un que de l\'autre.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_DEUX_POINTS },
        ],
    },

    // Une seule règle, sur l'OUTIL : les sept étapes disent déjà quoi tracer,
    // ce qui bloque l'élève c'est le geste du compas virtuel. Pas de figure,
    // elle serait la construction terminée.
    pb_centre_cercle_2mediatrices_programme: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Place la pointe du compas sur un point, puis fais glisser pour choisir le rayon. Les **deux cercles d\'une même couleur** se tracent avec le **même rayon**.',
                ],
            },
        ],
    },
    pb_figure_mixte_vf: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Dans un cercle, **tous les rayons sont égaux** : le centre est donc à égale distance de deux points du cercle, et il est **sur la médiatrice** de la corde qui les joint.',
                    'Être à **égale distance** de A et de B, c\'est être sur la **médiatrice** de [AB], pas sur le cercle. La médiatrice est une droite, elle sort du cercle.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_MEDIATRICE_HORS_CERCLE },
        ],
    },
    // Aucune règle écrite : les justifications à compléter SONT l'exercice.
    // ENTRÉE FONCTION : les six modèles décrivent des situations différentes (un
    // tracé au compas, l'intersection de deux médiatrices, une corde de cercle,
    // l'équidistance). Une fiche unique n'aiderait aucun d'eux. On choisit donc
    // la figure d'après la PHRASE du tirage, jamais d'après sa réponse.
    // Routage par sousType : mA et mF illustrent la même idée (deux médiatrices),
    // mB la corde, mD la construction au compas. mC et mE retombent sur le
    // défaut, l'équidistance, qui est bien leur sujet.
    pb_justification_trous: {
        variantes: [
            { sousType: 'mD', pages: [{ titre: 'Un exemple', svg: SVG_COMPAS }] },
            { sousType: 'mA', pages: [{ titre: 'Un exemple', svg: SVG_DEUX_MEDIATRICES }] },
            { sousType: 'mF', pages: [{ titre: 'Un exemple', svg: SVG_DEUX_MEDIATRICES }] },
            { sousType: 'mB', pages: [{ titre: 'Un exemple', svg: SVG_CORDE_RAYONS }] },
        ],
        defaut: { pages: [{ titre: 'Un exemple', svg: SVG_DEUX_POINTS }] },
    },
};

export default CP_660;
